import { BIDDER_RULE_SECTION_LABELS, BidderRule, BidderRuleSection, sectionOfBidderSeat } from '@pubstack/common/src/bidder-rule'
import { BidderSeat } from '@pubstack/common/src/bidder-seat'
import { BidderCatalog } from '@pubstack/common/src/bidderCatalog'
import { MEDIA_TYPES, MediaType } from '@pubstack/common/src/media-types'
import { byName, byNameOf } from '@pubstack/common/src/sort'
import { DisplayedHeaderBiddingRule } from '@pubstack/common/src/stack'

export type SelectedBidderSeats = BidderRule['selectedBidderSeats']

/**
 * The two sections the auto-add settings and the flyout draw, in reading order.
 *
 * Fewer than the model stores. The document keeps three — `client_side`, `server_side` and the
 * deprecated one — and the surfaces used to draw a fourth for Spark on top, which made a page of four
 * headings where three of them said "server-side" in different words. Where the bid request is sent
 * from is the distinction a publisher is making here; which server-side integration a given seat rides
 * is a property of that seat, so it goes on the row as a chip instead of splitting the list. The seat
 * table itself filters by the finer type instead — see `BidderRuleSeatTable`.
 */
export const BIDDER_RULE_FORM_SECTIONS = ['client_side', 'server_side'] as const
export type BidderRuleFormSection = (typeof BIDDER_RULE_FORM_SECTIONS)[number]

export const BIDDER_RULE_FORM_SECTION_LABELS: Record<BidderRuleFormSection, string> = { ...BIDDER_RULE_SECTION_LABELS }

/**
 * The persisted sections behind a displayed one — where its auto-add flags live.
 *
 * The merged server-side section answers for both of the document's server-side flags, so its one
 * control writes both: they are the same question asked of one list, and leaving the deprecated flag
 * without a control would have stranded it at whatever it happened to hold.
 */
export const storedSectionsOf = (section: BidderRuleFormSection): BidderRuleSection[] => (section === 'client_side' ? ['client_side'] : ['server_side', 'server_side_deprecated'])

/** The flag read back to draw the control — the current integration's, where a section has two. */
export const storedSectionOf = (section: BidderRuleFormSection): BidderRuleSection => (section === 'client_side' ? 'client_side' : 'server_side')

export const formSectionOfBidderSeat = (seat: Pick<BidderSeat, 'type' | 'platform'>): BidderRuleFormSection =>
  seat.type === 'spark_for_pubs' || sectionOfBidderSeat(seat) !== 'client_side' ? 'server_side' : 'client_side'

/**
 * Whether a section can be edited on this scope.
 *
 * `blocked` is a sentence, NOT a dropped selection: a One scope holding deprecated `-s2s` seats
 * must still save them unchanged. Everything below therefore edits the existing selection in place
 * rather than rebuilding it from the visible sections.
 */
export type SectionAvailability = 'active' | 'blocked'

export type ScopeCapabilities = {
  hasOne: boolean
  hasNode: boolean
  /** A One scope gets server-side demand through Spark without buying Node. */
  isSparkForPubAvailable: boolean
  /** Spark Demand, as the viewer sees it: the offer shown on the Spark for Pubs filter needs neither. */
  isSparkDemandAvailable: boolean
}

export const sectionAvailability = (section: BidderRuleFormSection, { hasOne, hasNode, isSparkForPubAvailable, isSparkDemandAvailable }: ScopeCapabilities): SectionAvailability => {
  switch (section) {
    case 'client_side':
      // Node deploys no client-side bidders of its own, so a Node-only scope cannot edit them.
      return hasOne ? 'active' : 'blocked'
    case 'server_side':
      // Any way in counts, now that the section holds all three server-side integrations: Node buys
      // the current one and the deprecated one, and either Spark product hands a One-only scope
      // server-side demand without it — Spark Demand's own seat sits in this section. Blocked only
      // where the scope has none of them.
      return hasNode || isSparkForPubAvailable || isSparkDemandAvailable ? 'active' : 'blocked'
  }
}

/** How the stack editor's flyout and the view flyout order a rule's seats, so a seat sits in the same place in both. */
export const bySeatName = byNameOf<{ seat: Pick<BidderSeat, 'name'> }>((row) => row.seat.name)

/**
 * A media type is `not_compatible` when the seat's catalog entry says so. A seat with no catalog
 * entry is treated as supporting everything — the same fallback stack generation applies, so the
 * editor never claims an incompatibility the generated config would not honour.
 */
export type MediaTypeState = 'enabled' | 'disabled' | 'not_compatible'

export const supportedMediaTypesOf = (seat: BidderSeat, catalogBidders: BidderCatalog[]): MediaType[] =>
  catalogBidders.find((bidder) => bidder.id === seat.bidderCode)?.supportedMediaTypes || Array.from(MEDIA_TYPES)

export const mediaTypeState = (seat: BidderSeat, mediaType: MediaType, selected: SelectedBidderSeats, catalogBidders: BidderCatalog[]): MediaTypeState => {
  if (!supportedMediaTypesOf(seat, catalogBidders).includes(mediaType)) return 'not_compatible'
  return selected.find((entry) => entry.id === seat.id)?.mediaTypes.includes(mediaType) ? 'enabled' : 'disabled'
}

/** A seat is enabled when the rule selects at least one of its media types. */
export const isSeatEnabled = (seat: BidderSeat, selected: SelectedBidderSeats): boolean => (selected.find((entry) => entry.id === seat.id)?.mediaTypes.length ?? 0) > 0

/**
 * Toggles one media type of one seat, leaving every other entry — including those in blocked
 * sections — exactly as it was. A seat left with no media type is dropped: absent is how the model
 * spells "disabled", so an empty entry would be a second, redundant encoding of it.
 */
export const toggleMediaType = (seat: BidderSeat, mediaType: MediaType, selected: SelectedBidderSeats, catalogBidders: BidderCatalog[]): SelectedBidderSeats => {
  if (!supportedMediaTypesOf(seat, catalogBidders).includes(mediaType)) return selected

  const current = selected.find((entry) => entry.id === seat.id)
  const nextMediaTypes = current?.mediaTypes.includes(mediaType) ? current.mediaTypes.filter((type) => type !== mediaType) : [...(current?.mediaTypes ?? []), mediaType]

  return withSeatMediaTypes(seat, nextMediaTypes, selected)
}

/** Enables a seat on every media type its catalog entry supports, or disables it outright. */
export const toggleSeat = (seat: BidderSeat, enabled: boolean, selected: SelectedBidderSeats, catalogBidders: BidderCatalog[]): SelectedBidderSeats =>
  withSeatMediaTypes(seat, enabled ? supportedMediaTypesOf(seat, catalogBidders) : [], selected)

/**
 * The media types a rule grants each bidder, keyed by the name the stack output uses.
 *
 * Stack outputs address bidders by `bidderSeat.name` (see `mappingToBidder`), while a rule holds
 * seat ids — so the seat list is the join between them. An aliased label has no seat of its own and
 * simply gets no entry, rather than borrowing the media types of the seat it rides.
 */
export const mediaTypesByBidderName = (rule: Pick<BidderRule, 'selectedBidderSeats'> | undefined, seats: BidderSeat[]): Record<string, MediaType[]> => {
  if (!rule) return {}
  const nameById = new Map(seats.map((seat) => [seat.id, seat.name]))
  return Object.fromEntries(
    rule.selectedBidderSeats.flatMap((entry) => {
      const name = nameById.get(entry.id)
      return name ? [[name, entry.mediaTypes] as const] : []
    })
  )
}

/**
 * A rule in the shape the stack views render, so a bidder rule hovers the same way a floor or lazy
 * loading rule does. Seats that no longer resolve are dropped rather than shown as a bare id.
 */
export const toDisplayedBidderRule = (rule: Pick<BidderRule, 'name' | 'selectedBidderSeats' | 'autoAddNewBidderSeats'>, seats: BidderSeat[]): DisplayedHeaderBiddingRule => {
  const nameById = new Map(seats.map((seat) => [seat.id, seat.name]))
  return {
    ruleName: rule.name,
    autoAddNewBidderSeats: rule.autoAddNewBidderSeats,
    bidders: rule.selectedBidderSeats
      .flatMap((entry) => {
        const name = nameById.get(entry.id)
        return name ? [{ name, mediaTypes: entry.mediaTypes }] : []
      })
      .sort(byName),
  }
}

/**
 * Enables or disables every seat of one section at once.
 *
 * Folded left-to-right over the existing selection rather than rebuilt, so seats outside `seats` —
 * the other sections, and any blocked one — are carried through untouched.
 */
export const toggleAllSeats = (seats: BidderSeat[], enabled: boolean, selected: SelectedBidderSeats, catalogBidders: BidderCatalog[]): SelectedBidderSeats =>
  seats.reduce((current, seat) => toggleSeat(seat, enabled, current, catalogBidders), selected)

const withSeatMediaTypes = (seat: BidderSeat, mediaTypes: MediaType[], selected: SelectedBidderSeats): SelectedBidderSeats => {
  const others = selected.filter((entry) => entry.id !== seat.id)
  // Keep MEDIA_TYPES' order rather than click order, so an unchanged selection re-serializes
  // identically and does not flag every stack using the rule as out of sync.
  const ordered = MEDIA_TYPES.filter((type) => mediaTypes.includes(type))
  return ordered.length > 0 ? [...others, { id: seat.id, mediaTypes: ordered }] : others
}
